/**
 * Поиск пунктов меню в поле быстрой навигации (разметка 29.10.26 11:48:
 * «иногда удобнее ввести текстом, чем в меню искать нужный пункт»; макет
 * Codex `docs/design/2026-10-29-nav-menu-search/`).
 *
 * Слово запроса ищется и в названии пункта, и в названии РАЗДЕЛА: «поставки»
 * выводит все пункты «Поставок», а не только те, где слово стоит в подписи.
 * Каждое слово запроса обязано найтись: «подсорт шт» — один пункт. Слово из
 * одной-двух букв — началом слова, из трёх и больше — и внутри: «отчет»
 * находит «Финотчёт». Порядок — как в меню.
 *
 * С командами CRM поиск не спорит: запрос с латинской буквы получает
 * строку «Команда CRM» первой (`crmQuery`), а пункты меню — под ней. Какие
 * команды бывают, клиент не знает и не угадывает — решает сервер.
 */

export type MenuGroup<T extends { label: string }> = { group: string; items: T[] };
export type MenuHit<T> = { group: string; item: T };

const norm = (s: string) => s.toLowerCase().replaceAll("ё", "е");
const SPLIT = /[\s\-–—·]+/;
const terms = (s: string) => norm(s).split(SPLIT).filter(Boolean);
const fits = (term: string, word: string) => (term.length >= 3 ? word.includes(term) : word.startsWith(term));

/** Запрос с латинской буквы можно отправить командой CRM. */
export const crmQuery = (query: string) => /^[a-z]/i.test(query.trim());

export function matchMenu<T extends { label: string }>(menu: MenuGroup<T>[], query: string): MenuHit<T>[] {
  const q = terms(query);
  if (q.length === 0) return [];
  const hits: MenuHit<T>[] = [];
  for (const g of menu) {
    for (const item of g.items) {
      const own = terms(`${g.group} ${item.label}`);
      if (q.every((t) => own.some((w) => fits(t, w)))) hits.push({ group: g.group, item });
    }
  }
  return hits;
}

/**
 * Куски подписи для подсветки: `[текст, совпал]`. Правило совпадения то же,
 * что у `matchMenu`, иначе подсвеченное разошлось бы с найденным. `ё` и `е`
 * одной длины, поэтому позиции в исходной строке и в нормализованной равны.
 */
export function marks(text: string, query: string): [string, boolean][] {
  const on = Array<boolean>(text.length).fill(false);
  const q = terms(query);
  for (const m of text.matchAll(/[^\s\-–—·]+/g)) {
    const word = norm(m[0]);
    for (const t of q) {
      if (t.length < 3) {
        if (word.startsWith(t)) on.fill(true, m.index, m.index + t.length);
        continue;
      }
      for (let at = word.indexOf(t); at >= 0; at = word.indexOf(t, at + 1)) {
        on.fill(true, m.index + at, m.index + at + t.length);
      }
    }
  }
  const out: [string, boolean][] = [];
  for (let i = 0; i < text.length; i += 1) {
    const last = out[out.length - 1];
    if (last && last[1] === on[i]) last[0] += text[i];
    else out.push([text[i]!, on[i]!]);
  }
  return out;
}
